/**
 * QuickZip Variant Controller
 * Standalone script for handling variant swatch options with dependent option filtering
 * Handles price updates, gallery changes, upsell updates, and option availability
 */

(function(global) {
  'use strict';

  function qzlog() {
    try {
      // Temporarily enable debug logging by default
      if (true || (global.localStorage && localStorage.getItem('qzDebug'))) {
        console.log.apply(console, ['[QZ VARIANT]'].concat([].slice.call(arguments)));
      }
    } catch(e) {}
  }

  /**
   * Main VariantController class
   */
  function VariantController(config) {
    this.config = Object.assign({
      productFormSelector: '[data-product-form]',
      swatchOptionsSelector: '.swatch_options',
      selectInputSelector: 'select[name="id"]',
      priceSelector: '.currentVariantPrice',
      moneyFormat: '{{amount}}',
      enableGallery: true,
      enableUpsells: true,
      enableInventoryCheck: true
    }, config || {});

    this.productData = null;
    this.variants = [];
    this.options = [];
    this.currentVariant = null;
    this.formEl = null;
    this.selectEl = null;
    this.swatchContainer = null;
    this.selectedOptions = {};
    this.variantInventory = {};
    this.whatsIncludedData = {};

    this.init();
  }

  VariantController.prototype = {
    init: function() {
      qzlog('=== Initializing VariantController ===');
      qzlog('Config:', this.config);
      
      // Set loading state immediately
      this.setLoadingState(true);
      
      this.findElements();
      if (!this.formEl) {
        qzlog('ERROR: Product form not found');
        this.setLoadingState(false);
        return;
      }
      qzlog('Form found, parsing product data...');
      this.parseProductData();
      this.parseWhatsIncludedData();
      qzlog('Setting up event listeners...');
      this.setupEventListeners();
      qzlog('Initializing state...');
      this.initializeState();
      
      // Complete loading
      this.completeLoading();
      qzlog('=== VariantController initialization complete ===');
    },

    findElements: function() {
      this.formEl = document.querySelector(this.config.productFormSelector);
      if (this.formEl) {
        this.selectEl = this.formEl.querySelector(this.config.selectInputSelector);
        this.swatchContainer = this.formEl.querySelector(this.config.swatchOptionsSelector);
      }
    },

    parseProductData: function() {
      if (!this.formEl) {
        qzlog('No form element found for parsing product data');
        return;
      }

      qzlog('Form element found:', this.formEl);
      qzlog('Form attributes:', this.formEl.attributes);

      // Check for product JSON first (direct approach)
      var productJson = this.formEl.getAttribute('data-product');
      if (productJson && productJson.length > 30) {
        qzlog('Raw product JSON attribute:', 'Found (' + productJson.length + ' chars)');
        try {
          this.productData = JSON.parse(productJson);
          this.variants = this.productData.variants || [];
          this.options = this.productData.options || [];
          qzlog('Successfully parsed product data:', this.productData.title, 'with', this.variants.length, 'variants');
          qzlog('Product options:', this.options);
          qzlog('First variant example:', this.variants[0]);
          return;
        } catch(e) {
          qzlog('Error parsing product data:', e);
          qzlog('Raw JSON that failed to parse:', productJson.substring(0, 500));
        }
      }

      // Fallback: use product handle and fetch via AJAX
      var productHandle = this.formEl.getAttribute('data-product-handle');
      var productId = this.formEl.getAttribute('data-product-id');
      
      qzlog('Fallback: Using product handle approach');
      qzlog('Product handle:', productHandle);
      qzlog('Product ID:', productId);
      
      if (productHandle) {
        this.fetchProductData(productHandle);
      } else {
        qzlog('No product handle or JSON data found');
        qzlog('Available attributes on form:');
        for (var i = 0; i < this.formEl.attributes.length; i++) {
          var attr = this.formEl.attributes[i];
          qzlog('  ' + attr.name + ': ' + (attr.value.length > 100 ? attr.value.substring(0, 100) + '...' : attr.value));
        }
      }

      // Parse variant inventory if available
      var inventoryJson = this.formEl.getAttribute('data-variant-inventory');
      if (inventoryJson) {
        try {
          var inventoryArray = JSON.parse(inventoryJson);
          this.variantInventory = {};
          inventoryArray.forEach(function(item) {
            this.variantInventory[item.id] = item;
          }, this);
          qzlog('Parsed inventory data for', Object.keys(this.variantInventory).length, 'variants');
        } catch(e) {
          qzlog('Error parsing inventory data:', e);
        }
      } else {
        qzlog('No variant inventory data found');
      }

      // Get money format
      this.config.moneyFormat = this.formEl.getAttribute('data-money-format') || this.config.moneyFormat;
      qzlog('Money format:', this.config.moneyFormat);
    },

    parseWhatsIncludedData: function() {
      try {
        var dataEl = document.getElementById('qz-whats-included-data');
        if (dataEl) {
          var rawText = dataEl.textContent || '{}';
          qzlog('Raw what\'s included JSON length:', rawText.length);
          qzlog('Raw what\'s included JSON preview:', rawText.substring(0, 200));
          this.whatsIncludedData = JSON.parse(rawText);
          qzlog('Parsed what\'s included data for', Object.keys(this.whatsIncludedData).length, 'variants');
          qzlog('Variant IDs with data:', Object.keys(this.whatsIncludedData));
          qzlog('First variant data example:', this.whatsIncludedData[Object.keys(this.whatsIncludedData)[0]]);
        } else {
          qzlog('No what\'s included data element found');
        }
      } catch(e) {
        qzlog('Error parsing what\'s included data:', e);
      }
    },

    fetchProductData: function(handle) {
      var self = this;
      qzlog('Fetching product data for handle:', handle);
      
      // Use Shopify's product JSON endpoint
      var xhr = new XMLHttpRequest();
      xhr.open('GET', '/products/' + handle + '.js', true);
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            try {
              self.productData = JSON.parse(xhr.responseText);
              self.variants = self.productData.variants || [];
              self.options = self.productData.options || [];
              qzlog('Successfully fetched product data:', self.productData.title, 'with', self.variants.length, 'variants');
              qzlog('Product options:', self.options);
              qzlog('First variant example:', self.variants[0]);
              
              // Re-initialize with the fetched data
              self.initializeState();
              self.updateUI();
              
              // Complete loading after async fetch
              self.completeLoading();
            } catch(e) {
              qzlog('Error parsing fetched product data:', e);
              self.setLoadingState(false);
            }
          } else {
            qzlog('Failed to fetch product data, status:', xhr.status);
            self.setLoadingState(false);
          }
        }
      };
      xhr.send();
    },

    setupEventListeners: function() {
      if (!this.swatchContainer) return;

      var self = this;

      // Listen for swatch option changes
      this.swatchContainer.addEventListener('change', function(e) {
        var input = e.target;
        if (input.type === 'radio' && input.name.indexOf('option-') === 0) {
          self.handleOptionChange(input);
        }
      });

      // Also listen for clicks on swatch labels (in case radio doesn't fire)
      this.swatchContainer.addEventListener('click', function(e) {
        var label = e.target.closest('label');
        if (label) {
          var input = document.getElementById(label.getAttribute('for'));
          if (input && input.type === 'radio' && input.name.indexOf('option-') === 0) {
            setTimeout(function() {
              if (input.checked) {
                self.handleOptionChange(input);
              }
            }, 10);
          }
        }
      });

      // Listen for direct select changes as fallback
      if (this.selectEl) {
        this.selectEl.addEventListener('change', function(e) {
          self.handleDirectVariantChange(e.target.value);
        });
      }
    },

    initializeState: function() {
      // First check if URL has a variant parameter
      var urlVariantSet = this.initializeFromUrl();
      
      if (!urlVariantSet) {
        // No URL variant, use normal initialization
        // Get initial selected options from checked radios
        this.updateSelectedOptionsFromDOM();
        
        // Find and set current variant
        this.updateCurrentVariant();
      }
      
      // Update option availability
      this.updateOptionAvailability();
      
      // Update UI
      this.updateUI();
    },

    updateSelectedOptionsFromDOM: function() {
      if (!this.swatchContainer) {
        qzlog('No swatch container found');
        return;
      }

      var inputs = this.swatchContainer.querySelectorAll('input[type="radio"]:checked');
      this.selectedOptions = {};

      qzlog('Found checked inputs:', inputs.length);
      
      inputs.forEach(function(input) {
        qzlog('Processing input:', input.name, '=', input.value);
        var optionMatch = input.name.match(/option-(\d+)/);
        if (optionMatch) {
          var optionIndex = parseInt(optionMatch[1]);
          this.selectedOptions[optionIndex] = input.value;
          qzlog('Set option', optionIndex, 'to', input.value);
        } else {
          qzlog('Input name does not match expected pattern:', input.name);
        }
      }, this);

      qzlog('Final selected options from DOM:', this.selectedOptions);
    },

    handleOptionChange: function(input) {
      qzlog('Option changed:', input.name, '=', input.value);

      // Update selected options
      var optionMatch = input.name.match(/option-(\d+)/);
      if (optionMatch) {
        var optionIndex = parseInt(optionMatch[1]);
        this.selectedOptions[optionIndex] = input.value;
        qzlog('Updated selectedOptions:', this.selectedOptions);
        
        // Auto-select subsequent options
        this.autoSelectSubsequentOptions(optionIndex);
      } else {
        qzlog('Warning: Option name does not match pattern:', input.name);
        // Try to update all selected options from DOM as fallback
        this.updateSelectedOptionsFromDOM();
      }

      // Update variant and UI
      this.updateCurrentVariant();
      this.updateOptionAvailability();
      this.updateUI();

      // Update URL parameter if we have a current variant
      if (this.currentVariant) {
        this.updateUrlParameter('variant', this.currentVariant.id);
      }

      // Trigger custom event
      this.triggerVariantChange();
    },

    handleDirectVariantChange: function(variantId, updateUrl) {
      // Default to updating URL unless explicitly set to false
      if (updateUrl === undefined) updateUrl = true;
      
      var variant = this.findVariantById(parseInt(variantId));
      if (variant) {
        this.currentVariant = variant;
        this.updateSelectedOptionsFromVariant(variant);
        this.updateSwatchesFromOptions();
        this.updateOptionAvailability();
        this.updateUI();
        this.triggerVariantChange();
        
        // Update URL parameter if requested
        if (updateUrl) {
          this.updateUrlParameter('variant', variantId);
        }
      }
    },

    updateCurrentVariant: function() {
      // Convert selected options to array format
      var optionValues = [];
      for (var i = 0; i < this.options.length; i++) {
        optionValues[i] = this.selectedOptions[i] || null;
      }

      qzlog('Converting selected options to array:', this.selectedOptions, '→', optionValues);
      qzlog('Available options structure:', this.options);
      qzlog('Total variants to search:', this.variants.length);

      // Find matching variant
      var matchingVariant = this.findVariantByOptions(optionValues);
      
      if (matchingVariant) {
        this.currentVariant = matchingVariant;
        qzlog('Found matching variant:', matchingVariant.id, matchingVariant.title);
      } else {
        // Try to find the best partial match
        var partialMatch = this.findBestPartialMatch(optionValues);
        if (partialMatch) {
          this.currentVariant = partialMatch;
          qzlog('Using partial match variant:', partialMatch.id, partialMatch.title);
        } else {
          qzlog('No matching variant found for options:', optionValues);
          qzlog('First few variants for comparison:', this.variants.slice(0, 3));
        }
      }

      // Update the select element
      if (this.selectEl && this.currentVariant) {
        this.selectEl.value = this.currentVariant.id;
      }
    },

    updateSelectedOptionsFromVariant: function(variant) {
      this.selectedOptions = {};
      variant.options.forEach(function(option, index) {
        this.selectedOptions[index] = option;
      }, this);
    },

    updateSwatchesFromOptions: function() {
      if (!this.swatchContainer) return;

      // Update radio button states
      Object.keys(this.selectedOptions).forEach(function(optionIndex) {
        var value = this.selectedOptions[optionIndex];
        var input = this.swatchContainer.querySelector('input[name="option-' + optionIndex + '"][value="' + value + '"]');
        if (input) {
          input.checked = true;
        }
      }, this);
    },

    autoSelectSubsequentOptions: function(changedOptionIndex) {
      if (!this.swatchContainer || !this.options.length) return;
      
      var self = this;
      qzlog('Auto-selecting subsequent options after option', changedOptionIndex, 'changed');
      
      // For each subsequent option (changedOptionIndex + 1 onwards)
      for (var nextOptionIndex = changedOptionIndex + 1; nextOptionIndex < this.options.length; nextOptionIndex++) {
        qzlog('Processing option index:', nextOptionIndex);
        
        // Find the first available option value for this position
        var firstAvailable = this.findFirstAvailableOption(nextOptionIndex);
        
        if (firstAvailable) {
          qzlog('Auto-selecting option', nextOptionIndex, 'to', firstAvailable);
          this.selectedOptions[nextOptionIndex] = firstAvailable;
          
          // Update the radio button in the DOM
          var input = this.swatchContainer.querySelector('input[name="option-' + nextOptionIndex + '"][value="' + firstAvailable + '"]');
          if (input && !input.disabled) {
            input.checked = true;
          }
        } else {
          qzlog('No available option found for index', nextOptionIndex);
          // Clear the selection if nothing is available
          delete this.selectedOptions[nextOptionIndex];
        }
      }
    },

    findFirstAvailableOption: function(optionIndex) {
      if (!this.swatchContainer) return null;
      
      var self = this;
      var swatchElements = this.swatchContainer.querySelectorAll('.swatch[data-option-index="' + optionIndex + '"] .swatch-element');
      
      // Iterate through all possible values for this option
      for (var i = 0; i < swatchElements.length; i++) {
        var swatchEl = swatchElements[i];
        var optionValue = swatchEl.getAttribute('data-value');
        if (!optionValue) continue;
        
        // Build test options array with current selections + this test value
        var testOptions = [];
        for (var j = 0; j < this.options.length; j++) {
          if (j < optionIndex) {
            // Use currently selected options for previous positions
            testOptions[j] = this.selectedOptions[j] || null;
          } else if (j === optionIndex) {
            // Test this value
            testOptions[j] = optionValue;
          } else {
            // Wildcard for subsequent positions
            testOptions[j] = null;
          }
        }
        
        // Check if this option value has available variants
        if (this.hasAvailableVariantForOptions(testOptions, optionIndex)) {
          qzlog('Found first available option at index', optionIndex, ':', optionValue);
          return optionValue;
        }
      }
      
      qzlog('No available option found at index', optionIndex);
      return null;
    },

    updateOptionAvailability: function() {
      if (!this.swatchContainer || !this.options.length) return;

      var self = this;
      
      // For each option position
      this.options.forEach(function(option, optionIndex) {
        var swatchElements = self.swatchContainer.querySelectorAll('.swatch[data-option-index="' + optionIndex + '"] .swatch-element');
        
        swatchElements.forEach(function(swatchEl) {
          var optionValue = swatchEl.getAttribute('data-value');
          if (!optionValue) return;

          // Create test option combination - only consider selections from previous options (hierarchical filtering)
          var testOptions = [];
          for (var i = 0; i < self.options.length; i++) {
            if (i === optionIndex) {
              testOptions[i] = optionValue;
            } else if (i < optionIndex) {
              // Only use selections from previous options (higher in hierarchy)
              testOptions[i] = self.selectedOptions[i] || null;
            } else {
              // Don't constrain by later options (lower in hierarchy)
              testOptions[i] = null;
            }
          }

          // Check if any variant exists with this combination
          var hasAvailableVariant = self.hasAvailableVariantForOptions(testOptions, optionIndex);
          
          // Update swatch element classes
          swatchEl.classList.toggle('available', hasAvailableVariant);
          swatchEl.classList.toggle('soldout', !hasAvailableVariant);
          
          // Only show sale indicators on the final option (last in hierarchy)
          var isFinalOption = optionIndex === self.options.length - 1;
          if (isFinalOption) {
            // Check if any variant with this combination is on sale
            var hasVariantOnSale = self.hasVariantOnSaleForOptions(testOptions, optionIndex);
            swatchEl.classList.toggle('on-sale', hasVariantOnSale);
            
            // Add or remove sale indicator
            self.updateSwatchSaleIndicator(swatchEl, hasVariantOnSale);
          } else {
            // Remove sale indicators from non-final options
            swatchEl.classList.remove('on-sale');
            self.updateSwatchSaleIndicator(swatchEl, false);
          }
          
          // Update input disabled state
          var input = swatchEl.closest('.swatch').querySelector('input[value="' + optionValue + '"]');
          if (input) {
            input.disabled = !hasAvailableVariant;
          }
        });
      });
    },

    hasAvailableVariantForOptions: function(testOptions, fixedOptionIndex) {
      var self = this;
      
      return this.variants.some(function(variant) {
        // Check if this variant matches our test options where specified
        var matches = true;
        
        for (var i = 0; i < testOptions.length; i++) {
          if (testOptions[i] !== null) {
            if (variant.options[i] !== testOptions[i]) {
              matches = false;
              break;
            }
          }
        }
        
        if (!matches) return false;
        
        // If we're checking inventory, ensure variant is available
        if (self.config.enableInventoryCheck) {
          return variant.available;
        }
        
        return true;
      });
    },

    hasVariantOnSaleForOptions: function(testOptions, fixedOptionIndex) {
      var self = this;
      
      return this.variants.some(function(variant) {
        // Check if this variant matches our test options where specified
        var matches = true;
        
        for (var i = 0; i < testOptions.length; i++) {
          if (testOptions[i] !== null) {
            if (variant.options[i] !== testOptions[i]) {
              matches = false;
              break;
            }
          }
        }
        
        if (!matches) return false;
        
        // Check if variant is available (don't show sale on unavailable variants)
        if (self.config.enableInventoryCheck && !variant.available) {
          return false;
        }
        
        // Check if variant is on sale
        return variant.compare_at_price && variant.compare_at_price > variant.price;
      });
    },

    findVariantByOptions: function(optionValues) {
      return this.variants.find(function(variant) {
        return variant.options.every(function(option, index) {
          return optionValues[index] === null || option === optionValues[index];
        });
      });
    },

    findBestPartialMatch: function(optionValues) {
      var bestMatch = null;
      var bestScore = -1;

      this.variants.forEach(function(variant) {
        var score = 0;
        var hasConflict = false;

        variant.options.forEach(function(option, index) {
          if (optionValues[index] !== null) {
            if (option === optionValues[index]) {
              score++;
            } else {
              hasConflict = true;
            }
          }
        });

        if (!hasConflict && score > bestScore) {
          bestScore = score;
          bestMatch = variant;
        }
      });

      return bestMatch;
    },

    findVariantById: function(id) {
      return this.variants.find(function(variant) {
        return variant.id === id;
      });
    },

    updateUI: function() {
      if (!this.currentVariant) return;

      this.updatePrice();
      this.updateSaleIndicator();
      
      if (this.config.enableGallery) {
        this.updateGallery();
      }
      
      if (this.config.enableUpsells) {
        this.updateUpsells();
      }

      this.updateInventoryDisplay();
      this.updateWhatsIncluded();
    },

    setLoadingState: function() {
      // Temporarily disable loading state to prevent width constraint issues
      qzlog('Loading state: DISABLED to prevent width issues');
      return;
      
      // Apply loading state to product container
      var container = this.formEl ? this.formEl.closest('.product') || this.formEl.parentElement : document.body;
      container.classList.remove('qz-variant-ready');
      container.classList.add('qz-variant-loading');
      
      // Set flag for gallery updates
      if (this.formEl) {
        this.formEl.setAttribute('data-qz-ready', 'false');
      }
      container.classList.remove('qz-gallery-ready');
      
      qzlog('Loading state: ON');
    },

    setReadyState: function() {
      // Set ready state without loading state system
      if (this.formEl) {
        this.formEl.setAttribute('data-qz-ready', 'true');
      }
      
      // Mark gallery as ready
      var gallery = document.getElementById('qz-variant-gallery');
      if (gallery) {
        gallery.removeAttribute('hidden');
        gallery.classList.add('qz-gallery-ready');
      }
      
      var container = this.formEl ? this.formEl.closest('.product') || this.formEl.parentElement : document.body;
      container.classList.add('qz-gallery-ready');
      
      // Trigger gallery initialization if needed
      if (typeof qzApplyVariantImages === 'function' && this.currentVariant && !this._galleryInitialized) {
        this._galleryInitialized = true;
        qzApplyVariantImages(this.currentVariant.id);
      }
      
      qzlog('Ready state: ON (loading state disabled)');
    },

    completeLoading: function() {
      var self = this;
      // Minimal delay to ensure DOM updates are complete
      setTimeout(function() {
        self.setLoadingState(false);
        
        // Set a flag that we're ready for gallery updates
        if (self.formEl) {
          self.formEl.setAttribute('data-qz-ready', 'true');
        }
        
        // Show gallery with minimal delay
        setTimeout(function() {
          // Remove hidden attribute and mark gallery as ready
          var gallery = document.getElementById('qz-variant-gallery');
          if (gallery) {
            gallery.removeAttribute('hidden');
            gallery.classList.add('qz-gallery-ready');
          }
          
          // Also mark container as ready
          var container = self.formEl ? self.formEl.closest('.product') || self.formEl.parentElement : document.body;
          container.classList.add('qz-gallery-ready');
          
          // Trigger gallery initialization if needed, but only once
          if (typeof qzApplyVariantImages === 'function' && self.currentVariant && !self._galleryInitialized) {
            self._galleryInitialized = true;
            qzApplyVariantImages(self.currentVariant.id);
          }
        }, 50);
      }, 10);
    },

    // URL Parameter Management
    getUrlParameter: function(name) {
      var urlParams = new URLSearchParams(window.location.search);
      return urlParams.get(name);
    },

    updateUrlParameter: function(name, value) {
      var url = new URL(window.location);
      if (value) {
        url.searchParams.set(name, value);
      } else {
        url.searchParams.delete(name);
      }
      // Update URL without triggering page reload
      window.history.replaceState({}, '', url.toString());
      qzlog('Updated URL parameter:', name, '=', value);
    },

    initializeFromUrl: function() {
      var variantId = this.getUrlParameter('variant');
      if (variantId && this.variants && this.variants.length > 0) {
        qzlog('Found variant parameter in URL:', variantId);
        var targetVariant = this.variants.find(function(v) { return v.id == variantId; });
        if (targetVariant) {
          qzlog('Setting variant from URL:', targetVariant.title);
          // Set the current variant
          this.currentVariant = targetVariant;
          // Update the selected options to match this variant
          this.updateSelectedOptionsFromVariant(targetVariant);
          // Update the swatch UI to reflect the selected options
          this.updateSwatchesFromOptions();
          qzlog('Updated swatches from URL variant');
          return true;
        } else {
          qzlog('Variant ID from URL not found in product variants');
        }
      }
      return false;
    },

    updatePrice: function() {
      if (!this.currentVariant) return;

      var priceElements = document.querySelectorAll(this.config.priceSelector);
      var formattedPrice = this.formatMoney(this.currentVariant.price);

      priceElements.forEach(function(el) {
        // Skip sticky price elements - they sync from main price
        if (el.closest('.qz-sticky-atc')) {
          return;
        }
        
        // Update the visible price text
        var priceSpan = el.querySelector('span[style*="display:none"]') || el;
        if (priceSpan) {
          priceSpan.innerHTML = formattedPrice;
          if (priceSpan.style.display === 'none') {
            priceSpan.style.display = '';
          }
        } else {
          el.innerHTML = formattedPrice;
        }
      });

      // Update price meta elements
      var priceMetaEl = document.querySelector('[itemprop="price"]');
      if (priceMetaEl) {
        priceMetaEl.setAttribute('content', (this.currentVariant.price / 100).toFixed(2));
      }

      // Show/hide sold out message
      var soldOutEls = document.querySelectorAll('.sold_out');
      soldOutEls.forEach(function(el) {
        el.textContent = this.currentVariant.available ? '' : 'Sold Out';
      }, this);

      // Update custom bundle pricing if the functions exist
      this.updateCustomPricing();

      qzlog('Updated price to:', formattedPrice);
    },

    updateCustomPricing: function() {
      if (!this.currentVariant) return;
      
      // Check if the custom pricing functions exist
      if (typeof qzPromo_getTierDetails === 'function' && typeof getBundleSellPriceStr === 'function') {
        try {
          var priceInDollars = this.currentVariant.price / 100.0;
          var compareAtPriceInDollars = this.currentVariant.compare_at_price ? 
                                       this.currentVariant.compare_at_price / 100.0 : 
                                       priceInDollars;
          
          qzlog('Updating custom pricing for variant:', this.currentVariant.id);
          qzlog('Price:', priceInDollars, 'Compare at:', compareAtPriceInDollars);
          
          var bundleSellPriceStr = getBundleSellPriceStr(
            qzPromo_getTierDetails(priceInDollars, compareAtPriceInDollars)
          );
          
          var bundlePriceElement = document.getElementById('getBundleSellPriceStr');
          if (bundlePriceElement) {
            bundlePriceElement.innerHTML = bundleSellPriceStr;
            qzlog('Updated custom bundle pricing');
          }
        } catch(e) {
          qzlog('Error updating custom pricing:', e);
        }
      } else {
        qzlog('Custom pricing functions not available');
      }
    },

    updateSaleIndicator: function() {
      // Disabled - we only show individual swatch sale tags now
      // Remove any existing large sale indicator
      var saleIndicator = document.querySelector('.qz-sale-indicator');
      if (saleIndicator) {
        saleIndicator.remove();
      }
    },

    updateSwatchSaleIndicator: function(swatchEl, isOnSale) {
      var existingSaleTag = swatchEl.querySelector('.qz-swatch-sale-tag');
      
      if (isOnSale) {
        if (!existingSaleTag) {
          // Create the sale tag for this swatch
          var saleTag = document.createElement('span');
          saleTag.className = 'qz-swatch-sale-tag';
          saleTag.textContent = 'SALE';
          swatchEl.appendChild(saleTag);
        }
      } else {
        if (existingSaleTag) {
          existingSaleTag.remove();
        }
      }
    },

    updateGallery: function() {
      if (!this.currentVariant) return;

      // Try to use the enhanced variant gallery system
      if (global.qzApplyVariantImages && typeof global.qzApplyVariantImages === 'function') {
        qzlog('Updating gallery for variant:', this.currentVariant.id);
        global.qzApplyVariantImages(this.currentVariant.id);
      }
    },

    updateUpsells: function() {
      // Trigger upsell update if the system exists
      var upsellEvent = new CustomEvent('variant:change', {
        detail: { variant: this.currentVariant }
      });
      document.dispatchEvent(upsellEvent);
    },

    updateInventoryDisplay: function() {
      if (!this.currentVariant || !this.config.enableInventoryCheck) return;

      var inventory = this.variantInventory[this.currentVariant.id];
      if (!inventory) return;

      // Update inventory left display
      var inventoryEl = document.querySelector('.items_left');
      if (inventoryEl && inventory.inventory_management && inventory.inventory_quantity > 0) {
        var threshold = 10; // You can make this configurable
        if (inventory.inventory_quantity <= threshold) {
          var message = inventory.inventory_quantity === 1 ? 
            'Very few left! Don\'t wait!' : 
            'Not many left! Order now';
          inventoryEl.textContent = message;
          inventoryEl.style.display = 'block';
        } else {
          inventoryEl.style.display = 'none';
        }
      }

      // Update quantity input max if needed
      var quantityInput = document.querySelector('input[name="quantity"]');
      if (quantityInput && inventory.inventory_management && inventory.inventory_policy === 'deny') {
        quantityInput.setAttribute('max', inventory.inventory_quantity);
      }
    },

    updateWhatsIncluded: function() {
      if (!this.currentVariant) return;

      var container = document.getElementById('qz-whats-included');
      if (!container) {
        qzlog('What\'s included container not found');
        return;
      }

      var link = container.querySelector('.qz-whats-included-link');
      if (!link) {
        qzlog('What\'s included link not found');
        return;
      }

      var variantId = String(this.currentVariant.id);
      var content = this.whatsIncludedData[variantId];

      qzlog('Updating what\'s included for variant:', variantId, 'Content length:', content ? content.length : 0);

      if (content && typeof content === 'string' && content.trim() !== '') {
        // Destroy existing tooltipster instance if it exists
        if (typeof jQuery !== 'undefined' && jQuery(link).data('tooltipster-ns')) {
          jQuery(link).tooltipster('destroy');
        }

        // Initialize tooltipster with content directly (not via title attribute)
        if (typeof jQuery !== 'undefined' && jQuery.fn.tooltipster) {
          jQuery(link).tooltipster({
            content: content,
            contentAsHTML: true,
            theme: 'tooltipster-default',
            trigger: 'click',
            interactive: true,
            maxWidth: 400,
            functionReady: function(instance, helper) {
              jQuery(helper.tooltip).addClass('whats-included');
            }
          });
        }

        container.style.display = 'block';
      } else {
        container.style.display = 'none';
      }
    },

    triggerVariantChange: function() {
      // Trigger custom events for other systems to listen to
      var event = new CustomEvent('qz:variant:change', {
        detail: {
          variant: this.currentVariant,
          selectedOptions: this.selectedOptions,
          controller: this
        }
      });
      document.dispatchEvent(event);

      // Also trigger the legacy event for compatibility
      var legacyEvent = new CustomEvent('variant:change', {
        detail: { variant: this.currentVariant }
      });
      document.dispatchEvent(legacyEvent);

      qzlog('Triggered variant change event for variant:', this.currentVariant ? this.currentVariant.id : 'none');
    },

    formatMoney: function(cents) {
      if (typeof cents !== 'number') return '';
      
      var money = (cents / 100).toFixed(2);
      var format = this.config.moneyFormat || '{{amount}}';
      
      // Handle different money formats
      if (format.indexOf('{{amount_with_currency}}') !== -1) {
        return format.replace('{{amount_with_currency}}', money + ' USD');
      } else if (format.indexOf('{{amount}}') !== -1) {
        return format.replace('{{amount}}', money);
      } else {
        return money;
      }
    },

    // Public API methods
    getCurrentVariant: function() {
      return this.currentVariant;
    },

    getSelectedOptions: function() {
      return Object.assign({}, this.selectedOptions);
    },

    selectVariant: function(variantId, updateUrl) {
      this.handleDirectVariantChange(variantId, updateUrl);
    },

    refresh: function() {
      this.initializeState();
    }
  };

  // Auto-initialize on DOM ready
  function autoInit() {
    qzlog('=== Auto-initialization starting ===');
    // Look for product forms and initialize controllers
    var forms = document.querySelectorAll('[data-product-form]');
    qzlog('Found', forms.length, 'forms with [data-product-form]');
    
    forms.forEach(function(form, index) {
      qzlog('Processing form', index + 1, ':', form.id || 'no-id', form);
      
      if (form._qzVariantController) {
        qzlog('Form already has controller, skipping');
        return;
      }
      
      // Mark this form as handled by our new system to prevent old theme scripts from interfering
      form.setAttribute('data-qz-variant-handled', 'true');
      form._qzVariantHandled = true;
      
      qzlog('Creating controller for form:', form.id);
      var controller = new VariantController({
        productFormSelector: '#' + form.id
      });
      
      // Store reference for later access
      form._qzVariantController = controller;
      qzlog('Controller created and stored for form:', form.id);
    });

    qzlog('=== Auto-initialized', forms.length, 'variant controllers ===');
  }

  // Initialize when DOM is ready (run early to prevent conflicts)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    // Run immediately if DOM is already loaded
    autoInit();
  }

  // Re-initialize on Shopify section load
  document.addEventListener('shopify:section:load', function() {
    setTimeout(autoInit, 50);
  });

  // Also run early to beat other scripts
  setTimeout(autoInit, 0);

  // Expose the constructor globally
  global.QzVariantController = VariantController;

  // Expose convenience methods
  global.qzGetVariantController = function(formSelector) {
    var form = document.querySelector(formSelector || '[data-product-form]');
    return form ? form._qzVariantController : null;
  };

  global.qzRefreshVariantControllers = function() {
    var forms = document.querySelectorAll('[data-product-form]');
    forms.forEach(function(form) {
      if (form._qzVariantController) {
        form._qzVariantController.refresh();
      }
    });
  };

  // Test URL variant parameter functionality
  global.qzTestVariantUrl = function(variantId) {
    var forms = document.querySelectorAll('[data-product-form]');
    if (forms.length > 0 && forms[0]._qzVariantController) {
      var controller = forms[0]._qzVariantController;
      if (variantId) {
        // Test setting a specific variant
        controller.selectVariant(variantId, true);
      } else {
        // Test reading from current URL
        controller.initializeFromUrl();
      }
    }
  };

  qzlog('QzVariantController loaded');

})(window);